/* CUSTOM_FREIGHTHUB_BY_CLAUDE (Ryan 2026-10-15, Freight Hub H3, decision F4) - the freight calculator.
   Planner: Freight-Hub/FREIGHT-HUB-PLANNER.md (root docs repo).

   A manual front end on what the agents already run, so Ryan can price a load himself on a phone call
   or check an agent's figure. A PURE CLIENT over existing rfq-worker routes, in this order:
     1. POST /freight-estimate {lines}        weight per line + where it came from, pallets, confidence,
                                              and every line it could not weigh (named, never guessed)
     2. POST /freight-density {units}         the freight class of those pallets
     3. POST /admin/freight-rate-cache/lookup the stored price, live:false (instant; never calls Rocket)
     4. POST /rocket-rate (button)            the SAME live Rocket rating the agents run, on the exact
                                              pallets, class and extras (Ryan 10-15: not the stored table's
                                              rounded live fallback). No ref, so nothing is stamped on a deal.
   Beside it, POST /freight-quote-inputs (the Workstation's profile engine): one grey line when the two
   engines disagree (Ryan 10-15). It saves nothing. Docks and the default extras come from the Hub's
   summary (custom-freight-hub.js fires `freight-hub:summary`); a ZIP can always be typed instead.

   Sits OUTSIDE #freight-hub-root (the Hub redraws that box) and is shown only to an admin session; the
   worker refuses these routes to anybody else anyway. */
(function () {
  'use strict';

  var EXTRAS = [
    ['residential', 'Residential'], ['liftGate', 'Lift gate'], ['insideDelivery', 'Inside delivery'],
    ['limitedAccess', 'Limited access'], ['deliveryAppointment', 'Appointment'], ['notify', 'Notify before delivery'],
  ];
  var FALLBACK_EXTRAS = ['residential', 'liftGate']; // the price table's own default until the summary arrives
  /* lib/freight-split.js RATE_RANK + the estimate's own two (measured skids, exact-name crates), in plain words */
  var SOURCE_WORDS = {
    measured: 'measured on real shipments', joint: 'solved from mixed shipments', split: 'its share of mixed pallets',
    site: 'the website weight', seed: 'the product freight profile', shopify: 'the Shopify catalog weight (never measured)',
    thickness: 'estimated from the thickness (never measured)', 'measured-units': 'its own measured skids',
    'exact-name': 'the weight in its name',
  };
  var STORED_WORDS = {
    hit: 'from the stored table', 'stale-hit': 'from the stored table (older than the limit)',
    'class-estimate': 'from the stored table, another class scaled by the learned ratio', fallback: 'from the nearest stored price',
    live: 'priced live by Rocket',
  };

  // ── pure helpers (exposed for tests) ────────────────────────────────────────────────────────

  function n0(v) { return v == null || !isFinite(v) ? '0' : Math.round(v).toLocaleString('en-US'); }
  function money(v) { return v == null || !isFinite(v) ? '-' : '$' + Number(v).toFixed(2); }
  function zip5(v) { var m = String(v == null ? '' : v).trim().match(/^(\d{5})(?:-\d{4})?$/); return m ? m[1] : null; }
  function squash(s) { return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]/g, ''); }

  /** The docks a supplier ships from (the worker's site list), the price table's chosen docks first. */
  function docksFor(vendor, choices, seedKeys) {
    var v = squash(vendor);
    if (!v) return [];
    var seed = seedKeys || [];
    return (choices || []).filter(function (c) { return c && c.supplier && v.indexOf(squash(c.supplier)) !== -1 && zip5(c.zip); })
      .slice().sort(function (a, b) {
        var ia = seed.indexOf(a.key), ib = seed.indexOf(b.key);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      });
  }

  /** The quantity's unit: rolls sell by the square foot. */
  function unitWord(title) { return /\broll\b/i.test(String(title || '')) ? 'sq ft' : 'each'; }

  /** A pallet in either shape the estimate uses ({length,width,height,weightLbs} or the log's {l,w,h,lb}). */
  function palletOf(p) {
    return {
      length: Number(p.length != null ? p.length : p.l), width: Number(p.width != null ? p.width : p.w),
      height: Number(p.height != null ? p.height : p.h), weightLbs: Number(p.weightLbs != null ? p.weightLbs : p.lb),
    };
  }

  /** "2 pallets: 48x40x48 in at 1,029 lb (x2)" */
  function palletWords(pallets) {
    var list = (pallets || []).map(palletOf);
    if (!list.length) return 'no pallets';
    var groups = [];
    list.forEach(function (p) {
      var k = p.length + 'x' + p.width + 'x' + p.height + ' in at ' + n0(p.weightLbs) + ' lb';
      var g = groups.filter(function (x) { return x.k === k; })[0];
      if (g) g.n++; else groups.push({ k: k, n: 1 });
    });
    return list.length + (list.length === 1 ? ' pallet: ' : ' pallets: ') + groups.map(function (g) { return g.k + (g.n > 1 ? ' (x' + g.n + ')' : ''); }).join('; ');
  }

  /** /rocket-rate items: identical pallets grouped, weight = the line's TOTAL pounds, one class (the governing one). */
  function rocketItems(pallets, cls) {
    var out = [];
    (pallets || []).map(palletOf).forEach(function (p) {
      var g = out.filter(function (x) { return x.length === p.length && x.width === p.width && x.height === p.height && x._each === p.weightLbs; })[0];
      if (g) { g.pieces++; g.weight = Math.round(g._each * g.pieces * 100) / 100; return; }
      out.push({ length: p.length, width: p.width, height: p.height, weight: p.weightLbs, class: String(cls), pieces: 1, _each: p.weightLbs });
    });
    return out.slice(0, 20).map(function (x) { return { length: x.length, width: x.width, height: x.height, weight: x.weight, class: x.class, pieces: x.pieces }; });
  }

  /** Every extra named, true or false, so /rocket-rate's own defaults (appointment ON) never add one unasked. */
  function accessorialsOf(extras) {
    var on = extras || [];
    var out = {};
    EXTRAS.forEach(function (e) { out[e[0]] = on.indexOf(e[0]) !== -1; });
    return out;
  }

  function rocketBody(originZip, destZip, pallets, cls, extras) {
    return { shipperZip: originZip, consigneeZip: destZip, items: rocketItems(pallets, cls), accessorials: accessorialsOf(extras) };
  }

  function lookupBody(originZip, destZip, totalLb, cls, extras, goodsValue) {
    var b = { originZip: originZip, destZip: destZip, weight: totalLb, class: String(cls), extras: (extras || []).slice(), live: false };
    var v = Number(goodsValue); // CUSTOM_FREIGHTH7_BY_CLAUDE (H7 C2) - the goods value prices the insurance when that switch is on
    if (isFinite(v) && v > 0) b.declaredValue = Math.round(v * 100) / 100;
    return b;
  }

  /** The stored price in one sentence, or what is missing. */
  function storedWords(r) {
    if (!r) return 'No stored price.';
    if (r.source === 'unavailable' || r.cost == null) return 'Nothing stored for this lane yet. Use "Price it live".';
    var s = money(r.cost) + ' cost, ' + money(r.charge) + ' with the ' + r.markupPct + '% markup, ' + (STORED_WORDS[r.source] || r.source)
      + (r.carrier ? ', ' + r.carrier : '') + (r.transitDays != null ? ', ' + r.transitDays + ' days' : '') + '.';
    var notes = ['priced as ' + n0(r.weightBand) + ' lb, class band ' + r.classBand];
    if (r.cell && r.cell.ageDays != null) notes.push(r.cell.ageDays + ' days old');
    if (r.fees && r.fees.parts && r.fees.parts.length) notes.push('extras ' + money(r.fees.amount));
    if (r.fees && r.fees.missing && r.fees.missing.length) notes.push('extras NOT priced yet: ' + r.fees.missing.join(', '));
    if (r.trueUp) notes.push('check against the real rate after the order');
    if (r.insurance) notes.push('includes ' + money(r.insurance.amount) + ' insurance (' + (r.insurance.basis === 'learned' ? r.insurance.ratePct + '% of the goods value' : r.insurance.basis === 'minimum-no-value' ? 'the $30 minimum: no goods value given' : 'the $30 minimum') + ')'); // CUSTOM_FREIGHTH7_BY_CLAUDE
    return s + ' (' + notes.join('; ') + ')' + (r.why ? ' ' + r.why : '');
  }

  /** The Workstation block's answer when it disagrees with the estimate (10% on weight, or a different unit count). */
  function compareWords(estLb, estPallets, qi) {
    if (!qi || !qi.quotable || !qi.shipment) return null;
    var lb = Number(qi.shipment.actualLbs), units = Number(qi.shipment.handlingUnits);
    if (!isFinite(lb) || lb <= 0) return null;
    var off = estLb > 0 ? Math.abs(lb - estLb) / estLb : 1;
    if (off <= 0.10 && units === estPallets) return null;
    return 'The Workstation freight block (product freight profiles) says ' + n0(lb) + ' lb on ' + units + ' unit' + (units === 1 ? '' : 's') + '.';
  }

  /** The live answer: cheapest carrier, cost and the marked-up charge. */
  function liveWords(r, markupPct) {
    if (!r || !r.cheapest) return 'Rocket returned no carrier rates for this lane and shape.' + (r && r.errors && r.errors.length ? ' (' + r.errors.slice(0, 2).join('; ') + ')' : '');
    var c = r.cheapest;
    var pct = isFinite(markupPct) ? Number(markupPct) : 5;
    return money(c.total) + ' cost, ' + c.carrier + (c.transitDays != null ? ', ' + c.transitDays + ' days' : '')
      + '. With the ' + pct + '% markup: ' + money(Math.round(c.total * (1 + pct / 100) * 100) / 100) + '.';
  }

  function errText(e) {
    if (!e) return 'unknown error';
    var d = e.data || {};
    if (Array.isArray(d.errors) && d.errors.length) return d.errors.map(function (x) { return typeof x === 'string' ? x : (x && x.message) || ''; }).filter(Boolean).join(' ');
    return e.message || 'request failed';
  }

  // ── DOM ─────────────────────────────────────────────────────────────────────────────────────

  function el(tag, attrs, kids) {
    var node = document.createElement(tag);
    if (attrs) for (var k in attrs) {
      if (!Object.prototype.hasOwnProperty.call(attrs, k) || attrs[k] == null) continue;
      if (k === 'text') node.textContent = attrs[k];
      else if (k === 'class') node.className = attrs[k];
      else if (k.indexOf('on') === 0) node.addEventListener(k.slice(2), attrs[k]);
      else node.setAttribute(k, attrs[k]);
    }
    (kids || []).forEach(function (c) { if (c) node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return node;
  }
  function clear(node) { while (node && node.firstChild) node.removeChild(node.firstChild); }
  function line(text, cls) { return el('p', { class: 'cfc-line' + (cls ? ' ' + cls : ''), text: text }); }
  function api() { return window.RepPortal && window.RepPortal.API; }

  var box = null;
  var state = { lines: [], docks: [], seedKeys: [], defaultExtras: FALLBACK_EXTRAS, markupPct: 5, extrasTouched: false };
  var refs = {};

  function applySummary(data) {
    var pt = data && data.sections && data.sections.priceTable;
    if (!pt || !pt.ok || !pt.data) return;
    var d = pt.data;
    var cfg = d.config || {};
    if (d.seed && Array.isArray(d.seed.dockChoices)) state.docks = d.seed.dockChoices;
    state.seedKeys = Array.isArray(cfg.seedDocks) ? cfg.seedDocks : [];
    if (Array.isArray(cfg.defaultExtras)) state.defaultExtras = cfg.defaultExtras;
    if (cfg.markupPct != null) state.markupPct = Number(cfg.markupPct);
    if (refs.from) fillFrom();
    if (refs.extras && !state.extrasTouched) setExtras(state.defaultExtras);
  }

  function fillFrom() {
    var keep = refs.from.value;
    clear(refs.from);
    refs.from.appendChild(el('option', { value: 'auto', text: "The supplier's dock" }));
    var docks = state.docks.slice().sort(function (a, b) {
      var ia = state.seedKeys.indexOf(a.key), ib = state.seedKeys.indexOf(b.key);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
    docks.forEach(function (d) { if (zip5(d.zip)) refs.from.appendChild(el('option', { value: d.zip, text: d.label + ' (' + d.zip + ')' })); });
    refs.from.appendChild(el('option', { value: 'zip', text: 'Type a ZIP' }));
    refs.from.value = keep && refs.from.querySelector('option[value="' + keep + '"]') ? keep : 'auto';
  }

  function setExtras(list) {
    var boxes = refs.extras.querySelectorAll('input[type=checkbox]');
    for (var i = 0; i < boxes.length; i++) boxes[i].checked = list.indexOf(boxes[i].value) !== -1;
  }
  function extrasNow() {
    var out = [];
    var boxes = refs.extras.querySelectorAll('input[type=checkbox]');
    for (var i = 0; i < boxes.length; i++) if (boxes[i].checked) out.push(boxes[i].value);
    return out;
  }

  function drawLines() {
    clear(refs.lines);
    if (!state.lines.length) { refs.lines.appendChild(line('No products yet. Search above and add them.', 'cfc-muted')); return; }
    state.lines.forEach(function (li, i) {
      var qty = el('input', { type: 'number', min: '0', step: 'any', class: 'cfc-input cfc-input--qty', value: String(li.qty), 'aria-label': 'Quantity' });
      qty.addEventListener('input', function () { li.qty = Number(qty.value); });
      refs.lines.appendChild(el('div', { class: 'cfc-row' }, [
        el('span', { class: 'cfc-row__title', text: li.title + (li.variantTitle ? ' (' + li.variantTitle + ')' : '') + ' - ' + (li.vendor || 'no supplier') }),
        qty, el('span', { class: 'cfc-muted', text: unitWord(li.title) }),
        el('button', { type: 'button', class: 'cfc-btn cfc-btn--ghost', text: 'Remove', onclick: function () { state.lines.splice(i, 1); drawLines(); } }),
      ]));
    });
  }

  function search() {
    var q = refs.q.value.trim();
    clear(refs.results);
    if (!q) return;
    refs.results.appendChild(line('Searching the catalog...', 'cfc-muted'));
    api().get('/product-search?q=' + encodeURIComponent(q)).then(function (r) {
      clear(refs.results);
      var products = (r && r.products) || [];
      if (!products.length) { refs.results.appendChild(line('No products match "' + q + '".', 'cfc-muted')); return; }
      products.slice(0, 12).forEach(function (p) { refs.results.appendChild(resultRow(p)); });
    }).catch(function (e) {
      clear(refs.results);
      refs.results.appendChild(line('The catalog search failed: ' + errText(e), 'cfc-bad'));
    });
  }

  function resultRow(p) {
    var variants = (p.variants || []).filter(function (v) { return v; });
    var pick = el('select', { class: 'cfc-input', 'aria-label': 'Variant' });
    variants.forEach(function (v, i) { pick.appendChild(el('option', { value: String(i), text: v.title && v.title !== 'Default Title' ? v.title : (v.sku || 'Default') })); });
    var qty = el('input', { type: 'number', min: '0', step: 'any', class: 'cfc-input cfc-input--qty', placeholder: unitWord(p.title), 'aria-label': 'Quantity' });
    return el('div', { class: 'cfc-row' }, [
      el('span', { class: 'cfc-row__title', text: p.title + ' - ' + (p.vendor || 'no supplier') }),
      variants.length > 1 ? pick : null,
      qty, el('span', { class: 'cfc-muted', text: unitWord(p.title) }),
      el('button', { type: 'button', class: 'cfc-btn cfc-btn--ghost', text: 'Add', onclick: function () {
        var v = variants[Number(pick.value) || 0] || {};
        var n = Number(qty.value);
        if (!(n > 0)) { qty.focus(); return; }
        state.lines.push({ handle: p.handle, sku: v.sku || null, title: p.title, variantTitle: v.title && v.title !== 'Default Title' ? v.title : '', vendor: p.vendor || '', qty: n });
        drawLines();
      } }),
    ]);
  }

  function originFor(vendor) {
    var v = refs.from.value;
    if (v === 'zip') { var z = zip5(refs.fromZip.value); return z ? { zip: z, label: 'ZIP ' + z } : null; }
    if (v !== 'auto') { var d = state.docks.filter(function (x) { return x.zip === v; })[0]; return { zip: v, label: d ? d.label : 'ZIP ' + v }; }
    var mine = docksFor(vendor, state.docks, state.seedKeys)[0];
    return mine ? { zip: mine.zip, label: mine.label } : null;
  }

  function estimate() {
    clear(refs.out);
    var dest = zip5(refs.to.value);
    var lines = state.lines.filter(function (l) { return l.qty > 0; });
    if (!lines.length) { refs.out.appendChild(line('Add at least one product with a quantity.', 'cfc-bad')); return; }
    if (!dest) { refs.out.appendChild(line('Type the 5-digit destination ZIP.', 'cfc-bad')); return; }
    if (refs.from.value === 'zip' && !zip5(refs.fromZip.value)) { refs.out.appendChild(line('Type the 5-digit ZIP it ships from.', 'cfc-bad')); return; }
    var extras = extrasNow();
    var body = lines.map(function (l) { return { handle: l.handle, sku: l.sku, qty: l.qty, vendor: l.vendor, title: l.title }; });
    refs.go.disabled = true;
    refs.out.appendChild(line('Estimating...', 'cfc-muted'));
    var qiP = api().post('/freight-quote-inputs', { lines: body.map(function (l) { return { handle: l.handle, sku: l.sku, qty: l.qty }; }) }).catch(function () { return null; });
    api().post('/freight-estimate', { lines: body, save: false }).then(function (est) {
      clear(refs.out);
      if (est.warning) refs.out.appendChild(line(est.warning, 'cfc-bad'));
      var shipments = est.shipments || [];
      if (!shipments.length) refs.out.appendChild(line('The estimate returned no shipments.', 'cfc-bad'));
      var totLb = 0, totPallets = 0;
      shipments.forEach(function (s) { totLb += s.totalLb || 0; totPallets += s.palletCount || 0; refs.out.appendChild(shipmentCard(s, dest, extras)); });
      var cmp = refs.out.appendChild(el('div'));
      qiP.then(function (qi) { var w = compareWords(totLb, totPallets, qi); if (w) cmp.appendChild(line(w, 'cfc-muted')); });
    }).catch(function (e) {
      clear(refs.out);
      refs.out.appendChild(line('The estimate failed: ' + errText(e) + '. This is a loading problem, not an empty result.', 'cfc-bad'));
    }).then(function () { refs.go.disabled = false; });
  }

  function shipmentCard(s, dest, extras) {
    var card = el('div', { class: 'cfc-ship' });
    var origin = originFor(s.vendor);
    card.appendChild(el('h3', { class: 'cfc-ship__title', text: (s.vendor || 'Unknown supplier') + (origin ? ' from ' + origin.label : '') }));
    (s.lines || []).forEach(function (l) {
      card.appendChild(line(l.title + ': ' + l.qty + ' x ' + (l.lbPerUnit != null ? (Math.round(l.lbPerUnit * 1000) / 1000) + ' lb' : '') + ' = ' + n0(l.lineLb) + ' lb, ' + (SOURCE_WORDS[l.rateSource] || l.rateSource) + (l.n ? ' (' + l.n + ' shipment' + (l.n === 1 ? '' : 's') + ')' : '')));
    });
    (s.missing || []).forEach(function (m) {
      card.appendChild(line('NOT WEIGHED: ' + (m.title || m.sku || m.key || 'a line') + (m.qty != null ? ' x ' + m.qty : '') + ' - ' + (m.why || 'no weight on record') + '. It is not in the weight below.', 'cfc-bad'));
    });
    var pallets = s.pallets || [];
    card.appendChild(line(n0(s.totalLb) + ' lb total. ' + palletWords(pallets) + '. Confidence: ' + (s.confidence || 'low') + '.', 'cfc-strong'));
    if (s.partialWhy) card.appendChild(line(s.partialWhy, 'cfc-bad'));
    if (s.askSupplier) card.appendChild(line('Ask the supplier: this does not fit a crate we have seen.', 'cfc-warn'));
    if (Array.isArray(s.basis) && s.basis.length) {
      var why = el('details', { class: 'cfc-why' }, [el('summary', { text: 'Why these numbers' })]);
      s.basis.forEach(function (b) { why.appendChild(line(b, 'cfc-muted')); });
      card.appendChild(why);
    }
    if (!pallets.length) { card.appendChild(line('Nothing to price: no pallet could be built.', 'cfc-bad')); return card; }
    if (!origin) { card.appendChild(line('No dock on file for ' + (s.vendor || 'this supplier') + '. Pick one under "Ships from" or type a ZIP.', 'cfc-bad')); return card; }
    var classLine = card.appendChild(line('Freight class...', 'cfc-muted'));
    var priceLine = card.appendChild(el('div'));
    var liveOut = el('div', { class: 'cfc-live' });
    api().post('/freight-density', { units: pallets.map(palletOf) }).then(function (d) {
      var cls = d.densityClass;
      classLine.textContent = 'Class ' + cls + ': ' + (d.summary || '') + '.';
      classLine.className = 'cfc-line';
      priceLine.appendChild(line('Stored price...', 'cfc-muted'));
      api().post('/admin/freight-rate-cache/lookup', lookupBody(origin.zip, dest, s.totalLb, cls, extras, refs.value && refs.value.value)).then(function (r) {
        clear(priceLine); priceLine.appendChild(line('Stored: ' + storedWords(r), r.cost == null ? 'cfc-muted' : 'cfc-strong'));
      }).catch(function (e) { clear(priceLine); priceLine.appendChild(line('Stored price failed: ' + errText(e), 'cfc-bad')); });
      var btn = el('button', { type: 'button', class: 'cfc-btn', text: 'Price it live' });
      btn.addEventListener('click', function () { priceLive(btn, liveOut, rocketBody(origin.zip, dest, pallets, cls, extras)); });
      card.appendChild(btn);
      card.appendChild(liveOut);
    }).catch(function (e) {
      classLine.textContent = 'Freight class failed: ' + errText(e) + '. No price without a class.';
      classLine.className = 'cfc-line cfc-bad';
    });
    return card;
  }

  function priceLive(btn, out, body) {
    btn.disabled = true;
    clear(out);
    out.appendChild(line('Asking Rocket... this usually takes 30 to 45 seconds, sometimes up to a minute. Nothing is saved to any deal.', 'cfc-muted'));
    var t0 = Date.now();
    api().post('/rocket-rate', body).then(function (r) {
      clear(out);
      out.appendChild(line('Live: ' + liveWords(r, state.markupPct), r.cheapest ? 'cfc-strong' : 'cfc-bad'));
      var rest = (r.rates || []).slice(1, 6);
      if (rest.length) out.appendChild(line('Next: ' + rest.map(function (x) { return x.carrier + ' ' + money(x.total) + (x.transitDays != null ? ' (' + x.transitDays + ' d)' : ''); }).join(', ') + '.', 'cfc-muted'));
      if (r.insurance && r.insurance.status === 'not-in-rate') out.appendChild(line('Insurance is not in these rates.', 'cfc-muted'));
      if (r.warning) out.appendChild(line(r.warning, 'cfc-warn'));
      out.appendChild(line('Rated ' + body.items.reduce(function (a, x) { return a + x.pieces; }, 0) + ' pallet(s) at class ' + (body.items[0] && body.items[0].class) + ' in ' + Math.round((Date.now() - t0) / 1000) + ' s. Same Rocket rating the agents run.', 'cfc-muted'));
    }).catch(function (e) {
      clear(out);
      out.appendChild(line('The live price failed: ' + errText(e), 'cfc-bad'));
    }).then(function () { btn.disabled = false; });
  }

  function build() {
    clear(box);
    refs.q = el('input', { type: 'search', class: 'cfc-input cfc-input--wide', placeholder: 'Search products (e.g. ecore 8mm roll)', 'aria-label': 'Search products' });
    refs.q.addEventListener('keydown', function (ev) { if (ev.key === 'Enter') { ev.preventDefault(); search(); } });
    refs.results = el('div', { class: 'cfc-results' });
    refs.lines = el('div', { class: 'cfc-lines' });
    refs.from = el('select', { class: 'cfc-input', 'aria-label': 'Ships from' });
    refs.fromZip = el('input', { type: 'text', inputmode: 'numeric', class: 'cfc-input cfc-input--zip', placeholder: 'ZIP', 'aria-label': 'Ships from ZIP', hidden: 'hidden' });
    refs.from.addEventListener('change', function () { refs.fromZip.hidden = refs.from.value !== 'zip'; });
    refs.to = el('input', { type: 'text', inputmode: 'numeric', class: 'cfc-input cfc-input--zip', placeholder: 'ZIP', 'aria-label': 'Destination ZIP' });
    refs.value = el('input', { type: 'text', inputmode: 'decimal', class: 'cfc-input cfc-input--zip', placeholder: '$', 'aria-label': 'Goods value for insurance' }); // CUSTOM_FREIGHTH7_BY_CLAUDE
    refs.extras = el('div', { class: 'cfc-checks' }, EXTRAS.map(function (e) {
      var cb = el('input', { type: 'checkbox', value: e[0] });
      cb.addEventListener('change', function () {
        state.extrasTouched = true;
        /* a residential delivery is always rated with a lift gate (the Short Form's own rule) */
        var lg = refs.extras.querySelector('input[value="liftGate"]');
        if (e[0] === 'residential' && cb.checked && lg) lg.checked = true;
        var res = refs.extras.querySelector('input[value="residential"]');
        if (e[0] === 'liftGate' && !cb.checked && res) res.checked = false;
      });
      return el('label', { class: 'cfc-check' }, [cb, ' ' + e[1]]);
    }));
    refs.go = el('button', { type: 'button', class: 'cfc-btn', text: 'Estimate', onclick: estimate });
    refs.out = el('div', { class: 'cfc-out', 'aria-live': 'polite' });
    box.appendChild(el('h2', { class: 'cfc-title', text: 'Price a load' }));
    box.appendChild(line('The same estimate, class and prices the agents use. It saves nothing.', 'cfc-muted'));
    box.appendChild(el('div', { class: 'cfc-row' }, [refs.q, el('button', { type: 'button', class: 'cfc-btn cfc-btn--ghost', text: 'Search', onclick: search })]));
    box.appendChild(refs.results);
    box.appendChild(refs.lines);
    box.appendChild(el('div', { class: 'cfc-row' }, [
      el('label', { class: 'cfc-field' }, ['Ships from ', refs.from, refs.fromZip]),
      el('label', { class: 'cfc-field' }, ['To ', refs.to]),
      el('label', { class: 'cfc-field' }, ['Goods value (insurance) ', refs.value]),
    ]));
    box.appendChild(refs.extras);
    box.appendChild(refs.go);
    box.appendChild(refs.out);
    fillFrom();
    setExtras(state.defaultExtras);
    drawLines();
  }

  // ── Boot ────────────────────────────────────────────────────────────────────────────────────

  var booted = false;
  function boot() {
    if (booted) return;
    box = document.getElementById('freight-hub-calc');
    if (!box || !api()) return;
    var sess = null;
    try { sess = JSON.parse(sessionStorage.getItem('rep_portal_session') || 'null'); } catch (e) { sess = null; }
    if (!sess) return; // still on the sign-in screen; `rep-portal:authenticated` retries
    booted = true;
    if (!sess.admin) return; // stays hidden; the Hub script shows the refusal
    build();
    box.hidden = false;
    if (window.FreightHub && window.FreightHub.lastSummary) applySummary(window.FreightHub.lastSummary);
  }

  window.FreightHubCalc = {
    _pure: { docksFor: docksFor, unitWord: unitWord, palletWords: palletWords, rocketItems: rocketItems, rocketBody: rocketBody, lookupBody: lookupBody, accessorialsOf: accessorialsOf, storedWords: storedWords, compareWords: compareWords, liveWords: liveWords, zip5: zip5, errText: errText },
    _boot: boot, _applySummary: applySummary,
  };

  if (typeof document !== 'undefined' && document.addEventListener) {
    window.addEventListener('freight-hub:summary', function (ev) { applySummary(ev && ev.detail); });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
    window.addEventListener('rep-portal:authenticated', boot);
  }
})();
/* END CUSTOM_FREIGHTHUB_BY_CLAUDE */
